// Importaciones necesarias desde React y otras dependencias
import { useEffect, useState } from "react";
import {
  GptMessage, // Componente para mostrar mensajes generados por GPT
  MyMessage, // Componente para mostrar mensajes del usuario
  TypingLoader, // Componente de animación mientras se carga una respuesta
  TextMessageBox, // Componente de la caja de texto donde el usuario escribe
} from "./index"; // Importar componentes personalizados
import { ReviAssistantApi } from "../../api/revi/reviAssistantApi";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks"; // Hook personalizado para manejar autenticación

const api = new ReviAssistantApi();

/**
 * Chat del asistente de REVI.
 *
 * El identificador de asistente ya no se usa: la personalidad la decide el
 * servidor a partir del rol que lleva el token. Se conserva en la firma porque
 * el componente se monta desde dos sitios y quitarlo ahora obligaria a tocar
 * los dos por algo que no cambia nada.
 */
export function NewAssistance({ assistantId, avatarImg }) {
  const { t } = useTranslation("misc");
  const storedImagePath = avatarImg;
  //console.log("NewAssistance -> assistantId", assistantId);
  // Definición de estados
  const [isLoading, setIsLoading] = useState(false); // Estado de carga
  const [messages, setMessages] = useState([]); // Estado para almacenar mensajes
  const [threadId, setThreadId] = useState(null); // Estado para almacenar el ID del hilo de conversación
  const { accessToken } = useAuth(); // Obtener el token de acceso desde la autenticación
  const [imageAvatar, setImageAvatar] = useState(null); // Estado para almacenar la ruta del avatar del asistente
  //console.log("NewAssistance -> imageAvatar", imageAvatar);
  // Efecto secundario que se ejecuta cuando se monta el componente o cuando cambian assistantId o accessToken
  useEffect(() => {
    // Función asincrónica para obtener datos y configurar el hilo de conversación
    const fetchData = async () => {
      if (!accessToken) return;
      try {
        setImageAvatar(storedImagePath);

        const res = await api.crearHilo(accessToken);
        setThreadId(res?.threadId || null);
        // Ya no se guarda en localStorage. Guardarlo servia para reusar el hilo
        // entre recargas, pero desde que el servidor no crea hilos al vuelo un
        // identificador viejo solo produce un 404 que nadie sabe leer. Abrir el
        // chat abre conversacion.
      } catch (error) {
        console.error("No se pudo abrir el asistente:", error);
      }
    };

    fetchData();
  }, [accessToken, storedImagePath]);

  // Función para manejar el envío de mensajes
  const handlePost = async (text) => {
    // Verificar si el ID del hilo está disponible
    if (!threadId) return;

    // Establecer estado de carga y agregar el mensaje del usuario a la lista de mensajes
    setIsLoading(true);
    setMessages((prev) => [...prev, { text: text, isGpt: false }]);

    try {
      const res = await api.enviar(accessToken, threadId, text);
      setIsLoading(false);

      const replies = res?.messages || [];
      let lastAssistantMessage = null;

      // Filtrar la última respuesta del asistente
      for (const reply of replies) {
        if (reply.role === "assistant") {
          lastAssistantMessage = reply.content[reply.content.length - 1];
        }
      }

      // Si hay una respuesta del asistente, agregarla a la lista de mensajes
      if (lastAssistantMessage) {
        setMessages((prev) => [
          ...prev,
          { text: lastAssistantMessage, isGpt: true },
        ]);
      }
    } catch (error) {
      setIsLoading(false);
      // El servidor manda un motivo por cada limite. Se ensena, en vez de
      // dejar al usuario mirando una pantalla que no responde.
      const motivo = error?.body?.reason;
      const texto =
        motivo === "hilo_agotado"
          ? t("assistant.hiloAgotado")
          : motivo === "mensaje_largo"
            ? t("assistant.mensajeLargo")
            : t("assistant.errorGenerico");
      setMessages((prev) => [...prev, { text: texto, isGpt: true }]);
    }
  };

  // Retornar la interfaz del componente
  return (
    <div className="chat-container">
      <div className="chat-messages">
        <div className="grid grid-cols-12 gap-y-2">
          {/* Saludo fijo de la interfaz. Lo que responde el asistente despues
              viene del modelo y NO se traduce aqui. */}
          <GptMessage
            text={t("assistant.greeting")}
            imageAvatar={imageAvatar}
          />

          {/* Renderizar los mensajes en la interfaz */}
          {messages.map((message, index) =>
            message.isGpt ? (
              <GptMessage
                key={index}
                text={message.text}
                imageAvatar={imageAvatar}
              />
            ) : (
              <MyMessage key={index} text={message.text} />
            ),
          )}

          {/* Mostrar la animación de carga si se está esperando una respuesta */}
          {isLoading && (
            <div className="col-start-1 col-end-12 fade-in">
              <TypingLoader />
            </div>
          )}
        </div>
      </div>

      {/* Componente de la caja de texto para enviar mensajes */}
      <TextMessageBox
        onSendMessage={handlePost}
        placeholder={t("assistant.inputPlaceholder")}
        disableCorrections
      />
    </div>
  );
}
